import { useCallback, useState } from 'react';
import { scrollMainToPageFeedback } from '../utils/mainScroll';

/**
 * Feedback unificado de acciones: mensaje arriba de la página + scroll al bloque #page-feedback.
 * Usar reportSuccess / reportError al terminar una acción; clearFeedback al iniciar o resetear UI.
 */
export function usePageFeedback() {
  const [success, setSuccessState] = useState('');
  const [error, setErrorState] = useState('');
  const [errorCode, setErrorCodeState] = useState(null);

  const clearError = useCallback(() => {
    setErrorState('');
    setErrorCodeState(null);
  }, []);

  const clearFeedback = useCallback(() => {
    setSuccessState('');
    clearError();
  }, [clearError]);

  const setSuccess = useCallback((message) => {
    setSuccessState(message);
    setErrorState('');
    setErrorCodeState(null);
  }, []);

  const setError = useCallback((message) => {
    setErrorState(message);
    setSuccessState('');
  }, []);

  const setErrorCode = useCallback((code) => {
    setErrorCodeState(code);
  }, []);

  const reportSuccess = useCallback((message, { scroll = true } = {}) => {
    setSuccess(message);
    if (scroll) scrollMainToPageFeedback();
  }, [setSuccess]);

  const reportError = useCallback((message, { scroll = true, code = null } = {}) => {
    setError(message);
    if (code != null) setErrorCodeState(code);
    else setErrorCodeState(null);
    if (scroll) scrollMainToPageFeedback();
  }, [setError]);

  /** Actualiza la página y devuelve { ok, message, code } para feedback en modales. */
  const reportActionResult = useCallback((
    { ok, message, code = null },
    { scroll = true } = {},
  ) => {
    const text = message || (ok ? 'Listo.' : 'Ocurrió un error.');
    if (ok) reportSuccess(text, { scroll });
    else reportError(text, { scroll, code });
    return { ok, message: text, code };
  }, [reportSuccess, reportError]);

  return {
    success,
    error,
    errorCode,
    clearFeedback,
    clearError,
    setSuccess,
    setError,
    setErrorCode,
    reportSuccess,
    reportError,
    reportActionResult,
  };
}
